import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  CandlestickSeries,
  ColorType,
  createChart,
  type CandlestickData,
  type IChartApi,
  type ISeriesApi,
  type UTCTimestamp,
} from 'lightweight-charts'
import type { Candle, CandleSeries } from '@nutrimatic/sdk'

import { cn } from '@/lib/utils'

type Props = {
  series: CandleSeries | null
  loading?: boolean
  error?: string | null
  height?: number
  className?: string
  /** Swap readout: canvas only, no glass chrome. */
  embedded?: boolean
  /** Grow to parent height (swap console / tablet). */
  fillHeight?: boolean
}

const CHART_IDLE = {
  handleScroll: {
    mouseWheel: false,
    pressedMouseMove: false,
    horzTouchDrag: false,
    vertTouchDrag: false,
  },
  handleScale: {
    mouseWheel: false,
    pinch: false,
    axisPressedMouseMove: false,
  },
} as const

const CHART_ACTIVE = {
  handleScroll: {
    mouseWheel: true,
    pressedMouseMove: true,
    horzTouchDrag: true,
    vertTouchDrag: true,
  },
  handleScale: {
    mouseWheel: true,
    pinch: true,
    axisPressedMouseMove: true,
  },
} as const

/**
 * Lightweight-charts requires strictly ascending unique times.
 * Deduplicate by bucket (last write wins) so partial tip never collides with a closed candle.
 */
function toCandleData(candles: readonly Candle[]): CandlestickData[] {
  const byTime = new Map<number, CandlestickData>()
  for (const c of candles) {
    const time = Number(c.time)
    if (!Number.isFinite(time)) continue
    byTime.set(time, {
      time: time as UTCTimestamp,
      open: c.open,
      high: c.high,
      low: c.low,
      close: c.close,
    })
  }
  return [...byTime.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([, candle]) => candle)
}

function candlesForSeries(series: CandleSeries): readonly Candle[] {
  // Prefer closed series; overlay partial only when it advances the open bucket.
  if (!series.partialCandle) return series.candles
  const partialTime = Number(series.partialCandle.time)
  const lastClosed = series.candles[series.candles.length - 1]
  if (lastClosed && Number(lastClosed.time) === partialTime) {
    return [...series.candles.slice(0, -1), series.partialCandle]
  }
  return [...series.candles, series.partialCandle]
}

/** Lab-parity OHLC chart with click-to-interact (DEX glass styling). */
export function NutrimaticCandleChart({
  series,
  loading = false,
  error = null,
  height = 320,
  className,
  embedded = false,
  fillHeight = false,
}: Props) {
  const hostRef = useRef<HTMLDivElement>(null)
  const shellRef = useRef<HTMLDivElement>(null)
  const chartRef = useRef<IChartApi | null>(null)
  const candleRef = useRef<ISeriesApi<'Candlestick'> | null>(null)
  const sizeRef = useRef({ w: 0, h: 0 })
  const fitRafRef = useRef<number | null>(null)
  const [interactive, setInteractive] = useState(false)
  const [renderError, setRenderError] = useState<string | null>(null)

  const drawn = useMemo(() => {
    if (!series) return null
    const candles = candlesForSeries(series)
    return {
      candles,
      interval: series.interval,
      priceAxisLabel: series.priceAxisLabel,
      count: candles.length,
    }
  }, [series])

  const drawnRef = useRef(drawn)
  drawnRef.current = drawn

  useEffect(() => {
    const host = hostRef.current
    if (!host) return

    const scheduleFit = () => {
      if (fitRafRef.current != null) cancelAnimationFrame(fitRafRef.current)
      // Double rAF: wait until flex/layout has committed real host size.
      fitRafRef.current = requestAnimationFrame(() => {
        fitRafRef.current = requestAnimationFrame(() => {
          fitRafRef.current = null
          const chart = chartRef.current
          if (!chart || !candleRef.current) return
          const el = hostRef.current
          if (!el || el.clientWidth < 8 || el.clientHeight < 8) return
          try {
            chart.timeScale().fitContent()
          } catch {
            /* ignore */
          }
        })
      })
    }

    const syncSize = (): boolean => {
      const el = hostRef.current
      const chart = chartRef.current
      if (!el || !chart) return false
      const w = el.clientWidth
      const h = fillHeight ? el.clientHeight : height
      if (w < 8 || h < 8) return false
      const prev = sizeRef.current
      const changed = Math.abs(prev.w - w) > 1 || Math.abs(prev.h - h) > 1
      if (!changed && prev.w > 0) return true
      sizeRef.current = { w, h }
      // With autoSize the library owns dimensions; only nudge manual charts.
      if (!fillHeight) {
        chart.applyOptions({ width: w, height })
      }
      return true
    }

    const initialHeight = fillHeight
      ? Math.max(host.clientHeight || 0, 80)
      : height
    const initialWidth = Math.max(host.clientWidth || 0, 0)

    const chart = createChart(host, {
      // autoSize follows the host once flex layout settles (swap console / tablet).
      autoSize: fillHeight,
      width: fillHeight ? undefined : initialWidth || undefined,
      height: fillHeight ? undefined : initialHeight,
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: 'rgba(148, 163, 184, 0.85)',
        fontSize: 11,
      },
      grid: {
        vertLines: { color: 'rgba(51, 65, 85, 0.25)' },
        horzLines: { color: 'rgba(51, 65, 85, 0.25)' },
      },
      rightPriceScale: { borderVisible: false },
      timeScale: {
        borderVisible: false,
        timeVisible: true,
        secondsVisible: false,
      },
      ...CHART_IDLE,
    })
    const candleSeries = chart.addSeries(CandlestickSeries, {
      upColor: '#00e8a3',
      downColor: '#f07178',
      borderVisible: false,
      wickUpColor: '#00e8a3',
      wickDownColor: '#f07178',
    })
    chartRef.current = chart
    candleRef.current = candleSeries
    sizeRef.current = { w: 0, h: 0 }

    const applyDrawn = () => {
      const current = drawnRef.current
      if (!current || current.candles.length === 0) return
      try {
        candleSeries.setData(toCandleData(current.candles))
        setRenderError(null)
        if (syncSize()) scheduleFit()
      } catch (e) {
        setRenderError(e instanceof Error ? e.message : 'Chart data error')
      }
    }

    applyDrawn()

    const ro = new ResizeObserver(() => {
      if (syncSize()) scheduleFit()
    })
    ro.observe(host)
    // Parent flex box often settles after the host mounts at 0×0.
    if (host.parentElement) ro.observe(host.parentElement)

    const bootFit = window.setTimeout(() => {
      if (syncSize()) scheduleFit()
    }, 0)

    return () => {
      window.clearTimeout(bootFit)
      if (fitRafRef.current != null) cancelAnimationFrame(fitRafRef.current)
      ro.disconnect()
      chart.remove()
      chartRef.current = null
      candleRef.current = null
      sizeRef.current = { w: 0, h: 0 }
    }
  }, [height, fillHeight])

  useEffect(() => {
    chartRef.current?.applyOptions(interactive ? CHART_ACTIVE : CHART_IDLE)
  }, [interactive])

  useEffect(() => {
    if (!candleRef.current || !chartRef.current) return
    if (!drawn || drawn.candles.length === 0) {
      try {
        candleRef.current.setData([])
      } catch {
        /* ignore clear failures */
      }
      return
    }
    try {
      candleRef.current.setData(toCandleData(drawn.candles))
      setRenderError(null)
      const el = hostRef.current
      const chart = chartRef.current
      if (el && chart && el.clientWidth >= 8 && el.clientHeight >= 8) {
        if (fitRafRef.current != null) cancelAnimationFrame(fitRafRef.current)
        fitRafRef.current = requestAnimationFrame(() => {
          fitRafRef.current = requestAnimationFrame(() => {
            fitRafRef.current = null
            try {
              chart.timeScale().fitContent()
            } catch {
              /* ignore */
            }
          })
        })
      }
    } catch (e) {
      setRenderError(e instanceof Error ? e.message : 'Chart data error')
    }
  }, [drawn])

  useEffect(() => {
    if (!interactive) return
    const onPointerDown = (event: PointerEvent) => {
      const shell = shellRef.current
      if (shell && !shell.contains(event.target as Node)) setInteractive(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setInteractive(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [interactive])

  const showChart = !(Boolean(error) && !series)
  const displayError = error ?? renderError

  const canvas = showChart ? (
    <div
      ref={shellRef}
      className={cn(
        'relative cursor-pointer rounded-lg',
        interactive && 'ring-1 ring-primary/40',
        embedded && 'h-full min-h-0',
      )}
      onClick={() => {
        if (!interactive) setInteractive(true)
      }}
      onKeyDown={(event) => {
        if (!interactive && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault()
          setInteractive(true)
        }
      }}
      role={interactive ? undefined : 'button'}
      tabIndex={0}
      aria-label={
        interactive
          ? 'Chart interactive. Press Escape or click outside to release.'
          : 'Click to interact with the chart'
      }
    >
      <div
        ref={hostRef}
        className={cn('w-full', embedded && 'h-full min-h-[5rem]')}
        style={
          embedded
            ? { opacity: loading && !series ? 0.4 : 1 }
            : { height, opacity: loading && !series ? 0.4 : 1 }
        }
      />
      {!embedded ? (
        <p className="mt-1 text-center text-[10px] text-muted-foreground">
          {interactive
            ? 'Chart active · Esc or click outside to release'
            : 'Click to zoom and pan · page scroll stays free'}
        </p>
      ) : null}
    </div>
  ) : null

  if (embedded) {
    return (
      <div className={cn('flex h-full min-h-0 flex-col', className)}>
        {displayError ? <p className="shrink-0 px-2 pt-1 text-xs text-loss">{displayError}</p> : null}
        <div className="min-h-0 flex-1 px-1 pb-1 sm:px-2">{canvas}</div>
      </div>
    )
  }

  return (
    <section className={cn('glass-chart flex flex-col overflow-hidden rounded-xl', className)}>
      <header className="glass-chart__header flex shrink-0 flex-wrap items-center justify-between gap-2 px-4 py-2.5">
        <h2 className="glass-chart__label text-xs font-semibold uppercase tracking-wider">Chart</h2>
        <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
          {loading ? 'loading · ' : null}
          {drawn ? (
            <>
              {drawn.interval} · {drawn.priceAxisLabel} · {drawn.count} candles
            </>
          ) : (
            '—'
          )}
        </p>
      </header>
      <div className="p-[var(--panel-padding)]">
        {displayError ? <p className="mb-2 text-sm text-loss">{displayError}</p> : null}
        {canvas}
      </div>
    </section>
  )
}
